<template>
  <div class="rental-list" :class="{ 'rental-list--indonesia': $i18n.locale === 'id-ID' }">
    <van-nav-bar :fixed="true" z-index="9" :title="title" :border="false" left-arrow @click-left="onClickLeft" />
    <div v-if="!isEmpty || error" class="rental-list__list">
      <van-pull-refresh
        v-model="refreshing"
        :pulling-text="$t('loading')"
        :loosing-text="$t('loading')"
        :loading-text="$t('loading')"
        success-text=""
        :success-duration="0"
        @refresh="onRefresh"
      >
        <van-list v-model="loading" :finished="finished" :finished-text="finishedText" :loading-text="$t('loading')" @load="onLoad">
          <RentalplanItem v-for="item in rentalList" :plan="item" :key="`${item.planId || item.rentalOrderId}-${item.period}`" @planClick="planClick">
          </RentalplanItem>
          <div v-if="rentalList.length" class="rental-list__secure-tip"><span class="rental-list__secure-icon">i</span> {{ $t('rentalplan.securePaymentNote') }}</div>
        </van-list>
      </van-pull-refresh>
    </div>
    <div class="rental-list__error" v-if="error">
      <p>{{ $t('systemError') }}</p>
      <div class="btn-primary btn-normal" @click="retryLoad">{{ $t('retry') }}</div>
    </div>
    <div class="rental-list__empty" v-if="isEmpty && !error">
      <div class="rental-list__empty-illustration" aria-hidden="true">
        <div class="rental-list__empty-icon">
          <span class="rental-list__receipt-icon"></span>
        </div>
      </div>
      <h2>{{ $t('rentalplan.empty') }}</h2>
      <p>{{ $t('rentalplan.emptyDesc') }}</p>
      <button type="button" class="rental-list__empty-action" @click="goShop">{{ $t('rentalplan.shopNow') }}</button>
      <div class="rental-list__secure-tip rental-list__secure-tip--empty"><span class="rental-list__secure-icon">i</span> {{ $t('rentalplan.securePaymentNote') }}</div>
    </div>
  </div>
</template>

<script>
import { List, PullRefresh } from 'vant';
import { mapState } from 'vuex';
import RentalplanItem from './components/RentalplanItem.vue';
import { queryRepayList } from '@/api/order';
import routerBack from '@/mixins/routerBack';
export default {
  name: 'RentalplanList',
  mixins: [routerBack],
  components: {
    RentalplanItem,
    [List.name]: List,
    [PullRefresh.name]: PullRefresh
  },
  computed: {
    ...mapState({
      accessToken: state => state.user.session?.accessToken || ''
    }),
    title() {
      return this.$t('accountPage.myBills');
    },
    finishedText() {
      return this.rentalList.length > 5 ? this.$t('noMore') : '';
    },
    isEmpty() {
      return this.rentalList.length === 0 && this.finished;
    }
  },
  data() {
    return {
      loading: false,
      finished: false,
      rentalList: [],
      pageNum: 1,
      pageSize: 20,
      error: false,
      loadingRequest: false,
      failedPageNum: 1,
      refreshing: false,
      requestSeq: 0,
      replacing: false,
      cachedAccessToken: '',
    };
  },
  activated() {
    this.syncAuthState();
    this.reloadFirstPageIfActive();
  },
  watch: {
    accessToken(token) {
      this.syncAuthState();
      if (token) {
        this.$nextTick(() => this.reloadFirstPageIfActive());
      }
    }
  },
  methods: {
    syncAuthState() {
      if (this.cachedAccessToken === this.accessToken) return;
      this.cachedAccessToken = this.accessToken;
      this.resetListState();
    },
    resetListState() {
      this.requestSeq++;
      this.loading = false;
      this.finished = false;
      this.rentalList = [];
      this.pageNum = 1;
      this.error = false;
      this.loadingRequest = false;
      this.failedPageNum = 1;
      this.refreshing = false;
      this.replacing = false;
    },
    reloadFirstPageIfActive() {
      if (!this.accessToken || this.$route.name !== 'RentalplanList' || this.loadingRequest || this.rentalList.length) return;
      this.onLoad();
    },
    onLoad() {
      this.syncAuthState();
      if (!this.accessToken) {
        this.resetListState();
        this.finished = true;
        return;
      }
      if (this.loadingRequest) return;
      this.loadingRequest = true;
      this.error = false;
      const requestSeq = ++this.requestSeq;
      const requestPage = this.pageNum;
      queryRepayList({ pageNum: requestPage, pageSize: this.pageSize }).then(res => {
        if (requestSeq !== this.requestSeq) return;
        const records = res?.code === 'Success' ? (res.data?.records || []) : [];
        if (res?.code !== 'Success') {
          this.error = true;
          this.failedPageNum = requestPage;
          this.finished = true;
          this.$toast(res?.message || this.$t('systemError'));
          return;
        }
        this.rentalList = this.replacing ? records : this.rentalList.concat(records);
        this.replacing = false;
        this.pageNum += 1;
        const total = Number(res.data?.total);
        this.finished = records.length < this.pageSize || (total > 0 && this.rentalList.length >= total);
      }).catch(() => {
        if (requestSeq !== this.requestSeq) return;
        this.error = true;
        this.failedPageNum = requestPage;
        this.finished = true;
        this.$toast(this.$t('systemError'));
      }).finally(() => {
        this.loadingRequest = false;
        this.loading = false;
        this.refreshing = false;
      });
    },
    onRefresh() {
      if (this.loadingRequest) return;
      this.pageNum = 1;
      this.finished = false;
      this.error = false;
      this.replacing = true;
      this.onLoad();
    },
    retryLoad() {
      if (this.loadingRequest) return;
      this.pageNum = this.failedPageNum || 1;
      this.finished = false;
      this.error = false;
      this.loading = false;
      this.loadingRequest = false;
      this.$nextTick(() => this.onLoad());
    },
    onClickLeft() {
      this.goBack()
    },
    planClick({ type, item }) {
      switch (type) {
        case 'planDetail':
          this.goDetail(item);
          break;
        case 'payNow':
          this.goDetailAndPay(item);
          break;
      }
    },
    goDetail(plan) {
      this.$router.push({
        name: 'RentalplanDetail',
        query: {
          orderId: plan.rentalOrderId
        }
      })
    },
    goDetailAndPay(plan) {
      const periods = Array.isArray(plan.periods) && plan.periods.length
        ? plan.periods
        : [Number(plan.period)].filter(period => period > 0);
      this.$router.push({
        name: 'RepayCheckout',
        query: {
          orderId: plan.rentalOrderId,
          periods: periods.join(','),
          amount: Number(plan.amount || 0),
          sourcePage: 'list'
        }
      });
    },
    goShop() {
      this.$router.push({ name: 'GoodsList' });
    }
  }
}
</script>

<style lang="less" scoped>
.rental-list {
  padding-top: 96px;
  min-height: 100vh;
  box-sizing: border-box;
  background: #F4F8FF;
  font-family: 'RentalInter', Helvetica, Arial, sans-serif;

  &__list { padding: 16px 16px 32px; }
  &__error { margin: 192px 24px 0; font-size: 26px; line-height: 36px; }

  &__empty {
    padding: 148px 32px 0;
    text-align: center;
  }

  &__empty > img {
    display: block;
    width: 240px;
    height: 240px;
    margin: 0 auto;
  }

  &__empty-illustration {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 240px;
    height: 240px;
    margin: 0 auto;
    border-radius: 20px;
    background: #EAF3FF;
  }

  &__empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 144px;
    height: 144px;
    border-radius: 20px;
    background: #FFFFFF;
  }

  &__receipt-icon {
    position: relative;
    display: block;
    width: 64px;
    height: 76px;
    border: 8px solid #0B45FF;
    border-top-width: 16px;
    border-radius: 2px 2px 8px 8px;
    box-sizing: border-box;
  }

  &__receipt-icon::before {
    position: absolute;
    top: -24px;
    left: -8px;
    width: 64px;
    height: 16px;
    background: repeating-linear-gradient(135deg, #0B45FF 0 8px, transparent 8px 16px);
    content: '';
  }

  &__receipt-icon::after {
    position: absolute;
    top: 14px;
    left: 10px;
    width: 34px;
    height: 18px;
    border-top: 6px solid #0B45FF;
    border-bottom: 6px solid #0B45FF;
    content: '';
  }

  &__empty h2 {
    margin: 40px 0 0;
    color: #070D3F;
    font-size: 36px;
    line-height: 52px;
    font-weight: 500;
  }

  &__empty p {
    max-width: 688px;
    margin: 16px auto 0;
    color: #6B7280;
    font-size: 26px;
    line-height: 36px;
    font-weight: 500;
  }

  &__empty > img + p {
    margin-top: 40px;
    color: #070D3F;
    font-size: 36px;
    line-height: 52px;
    font-weight: 500;
  }

  &__empty-action {
    display: block;
    width: 58%;
    max-width: 640px;
    height: 92px;
    margin: 48px auto 0;
    padding: 0;
    border: 0;
    border-radius: 16px;
    background: #0B45FF;
    color: #FFFFFF;
    font-size: 32px;
    line-height: 40px;
    font-weight: 500;
  }

  &__secure-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin: 16px 0 0;
    color: #6B7280;
    font-size: 24px;
    line-height: 36px;
    font-weight: 500;
    text-align: center;

  }

  &__secure-icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #6B7280;
    color: #FFFFFF;
    font-size: 20px;
    line-height: 28px;
    font-weight: 800;
  }

  &__secure-tip--empty {
    margin-top: 160px;
  }
}

.rental-list--indonesia {
  font-family: 'RentalPlusJakarta', Helvetica, Arial, sans-serif;
}

@font-face {
  font-family: 'RentalInter';
  src: url('~@/assets/fonts/inter.ttf') format('truetype');
  font-style: normal;
  font-weight: 100 900;
}

@font-face {
  font-family: 'RentalPlusJakarta';
  src: url('~@/assets/fonts/plus_jakarta_sans.ttf') format('truetype');
  font-style: normal;
  font-weight: 100 900;
}

.rental-list button {
  font-family: inherit;
}

::v-deep .van-nav-bar {
  height: 96px;
  background: #fff;
}

::v-deep .van-nav-bar__content {
  height: 96px;
}

::v-deep .van-nav-bar__title {
  max-width: calc(100% - 176px);
  color: #070D3F;
  font-size: 36px;
  line-height: 52px;
  font-weight: 500;
}

::v-deep .van-nav-bar__left {
  display: flex;
  align-items: center;
  width: 88px;
  height: 96px;
  padding: 0;
}

::v-deep .van-nav-bar__arrow {
  margin-left: 16px;
  color: #070D3F;
  font-size: 56px;
}

::v-deep .van-pull-refresh__text {
  font-size: 24px;
  line-height: 34px;
  font-weight: 400;
  color: #64748B;
}
</style>
